import React from 'react';
import {
  useObserver,
} from 'mobx-react';
import {
  useLocation,
} from 'react-router';
import * as yup from 'yup';
import GeneralFormView from '../../components/GeneralFormView';
import GeneralFormFields, {
  FormFieldActions,
} from '../../components/GeneralFormView/types';
import Condition, {
  ConditionStatement,
  ConditionalStatementTypes,
  ConditionExpression,
} from '../../components/GeneralFormView/Utils/Condition';
import {
  FormFieldsBuilder,
} from '../../components/GeneralFormView/Utils/FormFieldsBuilder';
import {
  RequestStatus,
  stores,
} from '../../mobx-store';
import {
  DeliveryRegionDTO,
} from './DTOs/deliveryRegionDTO';
import {
  DeliveryRegionFormViewModel,
} from './DTOs/deliveryRegionFormViewModel';

const AddEditDeliveryRegionScreen: React.FC = () => {
  const location = useLocation();
  const isEditing = location.pathname.endsWith('/edit');

  const mapToDTO = (formValue: DeliveryRegionFormViewModel): DeliveryRegionDTO => {
    const newDTO: DeliveryRegionDTO = {
      regionId: formValue.regionId,
      isActive: formValue.isActive,
      daysTillDelivery: formValue.daysTillDelivery,
    };
    if (formValue.regionName) {
      newDTO.regionName = formValue.regionName;
    }
    if (formValue.deliveryFees) {
      newDTO.deliveryFees = formValue.deliveryFees;
    }
    return newDTO;
  };

  const {
    formFields,
  } = new FormFieldsBuilder<DeliveryRegionFormViewModel>()
    .addField('regionId', {
      fieldOptions: {
      },
      hasTranslations: true,
      title: 'Region ID',
      type: GeneralFormFields.INPUT_FIELD,
      validationSchema: yup.number().required('Required'),
      condition: (values, actions) => new Condition({
        values,
        initialStatement: new ConditionStatement({
          action: actions[FormFieldActions.DISABLE],
          type: ConditionalStatementTypes.IF,
          expression: new ConditionExpression({
            values,
            expression: () => isEditing,
          }),
        }),
      }),
    })
    .addField('isActive', {
      hasTranslations: false,
      title: 'Active',
      type: GeneralFormFields.TOGGLE_FIELD,
      fieldOptions: {
      },
      validationSchema: yup.boolean().required(),
    })
    .addField('regionName', {
      fieldOptions: {
      },
      hasTranslations: false,
      title: 'Region Name',
      type: GeneralFormFields.INPUT_FIELD,
      validationSchema: yup.string().notRequired(),
    })
    .addField('daysTillDelivery', {
      hasTranslations: false,
      title: 'Delivery Period (in days)',
      type: GeneralFormFields.INPUT_FIELD,
      fieldOptions: {
      },
      validationSchema: yup.number().min(1).required('Required'),
    })
    .addField('deliveryFees', {
      hasTranslations: false,
      title: 'Delivery Fees',
      type: GeneralFormFields.INPUT_FIELD,
      fieldOptions: {
      },
      validationSchema: yup.number().min(0).notRequired(),
    });

  return useObserver(() => (
    <GeneralFormView<DeliveryRegionDTO, DeliveryRegionFormViewModel>
      defaultLang="en"
      title="Delivery Region"
      identifier="regionId"
      otherLanguages={[]}
      formData={formFields}
      create={async (formValue) => {
        const dto = mapToDTO(formValue);
        const res = await stores.deliveryRegionStore.createNewDeliveryRegion(dto);
        return res;
      }}
      update={async (id, formValue) => {
        const dto = mapToDTO(formValue);
        await stores.deliveryRegionStore.updateDeliveryRegionById(Number(id), dto);
      }}
      get={async (id) => {
        await stores.deliveryRegionStore.getDeliveryRegionById(Number(id));
        return new DeliveryRegionFormViewModel(stores.deliveryRegionStore.currentDeliveryRegion as DeliveryRegionDTO);
      }}
      isFetchSuccessful={stores.deliveryRegionStore.createOrUpdateStatus === RequestStatus.SUCCESS}
      createOrUpdateStatus={stores.deliveryRegionStore.createOrUpdateStatus}
    />
  ));
};

export default AddEditDeliveryRegionScreen;
